import { useState, useCallback, useRef } from "react";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function useCropSelection() {
  const [selection, setSelection] = useState<Rect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const startRef = useRef({ x: 0, y: 0 });

  const start = useCallback((x: number, y: number) => {
    startRef.current = { x, y };
    setSelection({ x, y, width: 0, height: 0 });
    setIsSelecting(true);
  }, []);

  const move = useCallback((x: number, y: number) => {
    if (!isSelecting) return;
    const { x: sx, y: sy } = startRef.current;
    const x1 = Math.min(sx, x);
    const y1 = Math.min(sy, y);
    const x2 = Math.max(sx, x);
    const y2 = Math.max(sy, y);
    setSelection({ x: x1, y: y1, width: x2 - x1, height: y2 - y1 });
  }, [isSelecting]);

  const end = useCallback(() => {
    setIsSelecting(false);
  }, []);

  const clear = useCallback(() => setSelection(null), []);

  /**
   * Crop the image (data URL) to the current selection.
   * displaySize: { width, height } of the displayed image (so we can map selection to source coords).
   */
  const getCroppedDataUrl = useCallback(
    async (
      imageDataUrl: string,
      displaySize: { width: number; height: number }
    ): Promise<string | null> => {
      if (!selection || selection.width < 4 || selection.height < 4) return null;

      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const sel = selection;
        img.onload = () => {
          const srcW = img.naturalWidth;
          const srcH = img.naturalHeight;
          const { width: dispW, height: dispH } = displaySize;
          const scaleX = srcW / dispW;
          const scaleY = srcH / dispH;
          const sx = Math.round(sel.x * scaleX);
          const sy = Math.round(sel.y * scaleY);
          const sw = Math.round(sel.width * scaleX);
          const sh = Math.round(sel.height * scaleY);
          const canvas = document.createElement("canvas");
          canvas.width = sw;
          canvas.height = sh;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null);
        img.src = imageDataUrl;
      });
    },
    [selection]
  );

  return {
    selection,
    isSelecting,
    start,
    move,
    end,
    clear,
    getCroppedDataUrl,
  };
}
